import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { useCreatePunchCardWithInvoice } from '@/hooks/usePatientPunchCards';
import { useActivePunchCardTemplates, useCreatePunchCardFromTemplate } from '@/hooks/usePunchCardTemplates';

interface CreatePunchCardModalProps {
  patientId: string;
  clinicId: string;
  onClose: () => void;
}

export function CreatePunchCardModal({
  patientId,
  clinicId,
  onClose,
}: CreatePunchCardModalProps) {
  const { t } = useTranslation();
  const createPunchCard = useCreatePunchCardWithInvoice();
  const createFromTemplate = useCreatePunchCardFromTemplate();
  const { data: templates = [] } = useActivePunchCardTemplates(clinicId);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [name, setName] = useState('');
  const [totalClips, setTotalClips] = useState(10);
  const [price, setPrice] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [createInvoice, setCreateInvoice] = useState(true);

  // When template is selected, pre-fill the form
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        setName(template.name);
        setTotalClips(template.total_clips);
        setPrice((template.price_cents / 100).toFixed(2));
        // Calculate expiry if template has expires_months
        if (template.expires_months) {
          const expiryDate = new Date();
          expiryDate.setMonth(expiryDate.getMonth() + template.expires_months);
          setExpiresAt(expiryDate.toISOString().split('T')[0]);
        } else {
          setExpiresAt('');
        }
      }
    }
  }, [selectedTemplateId, templates]);

  const handleSubmit = async () => {
    // If template is selected, use the quick template creation
    if (selectedTemplateId) {
      try {
        await createFromTemplate.mutateAsync({
          template_id: selectedTemplateId,
          patient_id: patientId,
          create_invoice: createInvoice,
        });

        toast.success(t('patients.punch_cards.created_success', 'Punch card created successfully'));
        onClose();
        return;
      } catch (error) {
        toast.error(t('patients.punch_cards.create_error', 'Failed to create punch card'));
        return;
      }
    }

    // Otherwise, use manual creation
    if (!name.trim()) {
      toast.error(t('patients.punch_cards.name_required', 'Please enter a name'));
      return;
    }

    if (totalClips < 1 || totalClips > 100) {
      toast.error(t('patients.punch_cards.clips_range_error', 'Total clips must be between 1 and 100'));
      return;
    }

    const priceValue = parseFloat(price);
    if (isNaN(priceValue) || priceValue < 0) {
      toast.error(t('patients.punch_cards.price_required', 'Please enter a valid price'));
      return;
    }

    const priceCents = Math.round(priceValue * 100);

    try {
      await createPunchCard.mutateAsync({
        clinic_id: clinicId,
        patient_id: patientId,
        name: name.trim(),
        total_clips: totalClips,
        price_cents: priceCents,
        currency: 'DKK',
        expires_at: expiresAt || null,
        appointment_types: [],
        group_types: [],
        create_invoice: createInvoice,
      });

      toast.success(t('patients.punch_cards.created_success', 'Punch card created successfully'));
      onClose();
    } catch (error) {
      toast.error(t('patients.punch_cards.create_error', 'Failed to create punch card'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 dark:bg-black/50">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('patients.punch_cards.new_card', 'New Punch Card')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="sr-only">{t('common.close', 'Close')}</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-5 p-6">
          {/* Template Selector */}
          {templates.length > 0 && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-900/20">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('patients.punch_cards.use_template', 'Quick Create from Template')}
                </label>
              </div>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">
                  {t('patients.punch_cards.select_template', 'Select a template or create manually')}
                </option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} — {template.total_clips} klip — {(template.price_cents / 100).toFixed(0)} kr
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="rounded-lg bg-indigo-50 p-4 dark:bg-indigo-900/20">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/30">
                <Ticket className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedTemplateId
                    ? t('patients.punch_cards.template_selected', 'Template Selected - Review & Confirm')
                    : t('patients.punch_cards.create_title', 'Create Punch Card')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('patients.punch_cards.create_description', 'Set up a new punch card for this patient')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('patients.punch_cards.name_label', 'Name')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('patients.punch_cards.name_placeholder', 'e.g., 10-clip card for massage')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('patients.punch_cards.total_clips_label', 'Total Clips')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={totalClips}
              onChange={(e) => setTotalClips(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('patients.punch_cards.price_label', 'Price (DKK)')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="500.00"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('patients.punch_cards.expires_label', 'Expiration Date')}
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="create-invoice"
              checked={createInvoice}
              onChange={(e) => setCreateInvoice(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
            />
            <label
              htmlFor="create-invoice"
              className="text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('patients.punch_cards.create_invoice_label', 'Create invoice automatically')}
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} disabled={createPunchCard.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createPunchCard.isPending || !name.trim() || !price}
          >
            {createPunchCard.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('patients.punch_cards.creating', 'Creating...')}
              </>
            ) : (
              t('patients.punch_cards.create', 'Create')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
